import { useCallback, useEffect, useState } from "react";
import { api, onUnauthorized, unwrap } from "./api/client";
import type { AuthStatus, Me } from "./api/types";
import { AccessScreen, PrivateScreen } from "./components/AccessScreens";
import { ContactsProvider } from "./components/Contacts";
import { LoginScreen, SetupScreen, TotpRequiredScreen } from "./components/AuthScreens";
import { InvitationScreen, SignupConfirmScreen, SignupScreen } from "./components/SignupScreens";
import { Shell } from "./components/Shell";
import { EmptyState, ToastProvider } from "./components/ui";
import { AuthProvider, isAdmin } from "./lib/auth";
import { hasServerMessages, setLang, setServerMessages, t, takePublicChoice, useLang } from "./lib/i18n";
import { LiveProvider, useLive } from "./lib/live";
import { href, PUBLIC_PAGES, useRoute } from "./lib/router";
import { Dashboard } from "./pages/Dashboard";
import { InputDetail } from "./pages/InputDetail";
import { Inputs } from "./pages/Inputs";
import { Journal } from "./pages/Journal";
import { Matrix } from "./pages/Matrix";
import { Outputs } from "./pages/Outputs";
import { Requests } from "./pages/Requests";
import { Settings } from "./pages/Settings";
import { Users } from "./pages/Users";

function Pages({ me }: { me: Me }) {
  const route = useRoute();
  const { state, connection } = useLive();
  let page;
  if (route.page === "users" || route.page === "settings" || route.page === "requests") {
    page = isAdmin(me.role) ? (route.page === "users" ? <Users /> : route.page === "requests" ? <Requests /> : <Settings />) : (
      <EmptyState title={t("Réservé aux administrateurs")}
        action={<a className="text-accent hover:underline" href={href({ page: "dashboard" })}>{t("Retour au tableau de bord")}</a>} />
    );
  } else if (!state) {
    page = (
      <div className="grid place-items-center py-24 text-muted">
        {connection === "closed" ? t("Le serveur de la gateway ne répond pas. Nouvel essai en cours…") : t("Connexion à la gateway…")}
      </div>
    );
  } else {
    switch (route.page) {
      case "dashboard": page = <Dashboard state={state} />; break;
      case "inputs": page = <Inputs state={state} />; break;
      case "input": page = <InputDetail key={route.id} state={state} id={route.id} />; break;
      case "outputs": page = <Outputs state={state} />; break;
      case "matrix": page = <Matrix state={state} />; break;
      case "journal": page = <Journal state={state} />; break;
    }
  }
  return <Shell route={route}>{page}</Shell>;
}

export function App() {
  const route = useRoute();
  const lang = useLang();   // tout est redessiné dans la nouvelle langue quand elle change
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [error, setError] = useState(false);
  // change à chaque nouvelle session : le WebSocket se reconnecte avec les droits à jour
  const [sessionVersion, setSessionVersion] = useState(0);

  const refresh = useCallback(() => {
    unwrap(api.GET("/api/auth/status"))
      .then((s) => {
        if (s.user?.lang) setLang(s.user.lang);   // la langue du compte l'emporte sur celle du navigateur
        setStatus(s);
        setError(false);
        setSessionVersion((v) => v + 1);
      })
      .catch(() => setError(true));
  }, []);

  useEffect(refresh, [refresh]);
  // journal, alarmes, erreurs des sorties : traduits à l'affichage avec le catalogue du serveur (connecté seulement)
  const loggedIn = Boolean(status?.user);
  useEffect(() => {
    if (lang !== "en" || !loggedIn || hasServerMessages()) return;
    unwrap(api.GET("/api/i18n/{lang}", { params: { path: { lang } } })).then(setServerMessages).catch(() => {});
  }, [lang, loggedIn]);
  useEffect(() => onUnauthorized(refresh), [refresh]);
  // titre de l'onglet : le nom de la gateway seulement pour un navigateur qui a le droit de la voir (accès privé)
  useEffect(() => {
    if (status && !status.access_required) document.title = "Gateway Live";
  }, [status]);

  const signedIn = (me: Me) => {
    const chosen = takePublicChoice();
    if (chosen && chosen !== me.lang) {
      // choisie sur la page de connexion : elle devient celle du compte
      api.PUT("/api/auth/language", { body: { lang: chosen } }).catch(() => {});
      me = { ...me, lang: chosen };
    } else if (me.lang) {
      setLang(me.lang);
    }
    setStatus((s) => ({ user: me, setup_required: false, setup_allowed: false, signup_enabled: s?.signup_enabled ?? false,
      access_required: false, contacts: s?.contacts ?? null }));
    setSessionVersion((v) => v + 1);
  };
  const logout = useCallback(() => {
    api.POST("/api/auth/logout").finally(() => setStatus((s) => (s ? { ...s, user: null } : s)));
  }, []);

  let content;
  if (error) {
    content = <div className="grid min-h-screen place-items-center text-muted">{t("Le serveur de la gateway ne répond pas. Rechargez la page dans un instant.")}</div>;
  } else if (!status) {
    content = <div className="grid min-h-screen place-items-center text-muted">{t("Chargement…")}</div>;
  } else if (PUBLIC_PAGES.includes(route.page)) {
    // liens des e-mails (inscription, invitation, accès personnel) : accessibles sans connexion
    content = route.page === "signup-confirm" ? <SignupConfirmScreen token={route.token} />
      : route.page === "invitation" ? <InvitationScreen token={route.token} onDone={signedIn} />
      : route.page === "access" ? <AccessScreen token={route.token} onDone={refresh} />
      : status.access_required && !status.signup_enabled ? <PrivateScreen />   // rien à montrer, pas même le nom
      : <SignupScreen enabled={status.signup_enabled} privateMode={status.access_required} />;
  } else if (status.access_required) {
    // accès privé, navigateur non autorisé : ni connexion, ni nom de la gateway
    content = status.signup_enabled ? <SignupScreen enabled privateMode /> : <PrivateScreen />;
  } else if (status.setup_required) {
    content = <SetupScreen allowed={status.setup_allowed} onDone={signedIn} />;
  } else if (!status.user) {
    content = <LoginScreen onLoggedIn={signedIn} signupEnabled={status.signup_enabled} />;
  } else if (status.user.totp_setup_required) {
    content = <TotpRequiredScreen me={status.user} onEnabled={signedIn} onLogout={logout} />;
  } else {
    content = (
      <AuthProvider value={{ me: status.user, logout, signedIn }}>
        <LiveProvider authVersion={sessionVersion}>
          <Pages me={status.user} />
        </LiveProvider>
      </AuthProvider>
    );
  }
  return <ToastProvider><ContactsProvider value={status?.contacts ?? null}>{content}</ContactsProvider></ToastProvider>;
}
